import {
    ConfigureRtcUtilsSettings,
    GxtRemoteConfigData,
    Log,
    OverrideConfigTypeEnum,
    RemoteOverrideInfo,
    toBool
} from "./dependencies";
/* Note: For public client usage, please skip to RemoteConfigData interface definition. */

const LOGTAG = "sessioncontrolsettings";

interface _SessionControlSettings {
    commonConfig: CommonConfig; // parameters for communication with the PM
    remoteOverrideInfo: RemoteOverrideInfo; // remote override config details for telemetry
    loggingEnabled: boolean; // Disable log events to client.
    consoleLoggingEnabled: boolean; // Enable log events to UA console. Requires loggingEnabled.
    webSocketSignaling: boolean; // Use WebSockets for signaling communication.
    webRtcStreamer: boolean; // Set GSStreamer metadata to WebRTC, no matter the platform name
    hdr?: boolean; // Whether or not to request an HDR stream
}

declare interface PmCommunication {
    httpBackOffDelay?: number;
    httpRetryCount?: number;
    httpDataReceiveTimeout?: number;
    httpConnectionTimeout?: number;
    pollingIntervalMin?: number;
    pollingIntervalMax?: number;
    pollingIntervalStep?: number;
    pollingQueueSizePerStep?: number;
    adsPollingIntervalMinMS?: number;
}

export declare interface CommonConfig {
    pmCommunication?: PmCommunication;
}

/* Note: This variable is not meant to be used by public Clients.
 */
export let SessionControlSettings: _SessionControlSettings = {
    commonConfig: {},
    remoteOverrideInfo: {
        type: OverrideConfigTypeEnum.UNKNOWN,
        version: ""
    },
    // todo: loggingEnabled and consoleLoggingEnabled are not used anywhere.
    loggingEnabled: true,
    consoleLoggingEnabled: false,
    webSocketSignaling: true,
    webRtcStreamer: false
};

/** Configuration parameters for the SessionControl library.*/
export declare interface SessionControlConfigData {
    /* GXT configurations fetched from server. Client is expected to pass entire GxT data without interpreting the data.*/
    gxtOverrideData?: string;
    /* Options set by dev through internal override tools. Options are in key=value format and separated by '&'. */
    overrideData?: string;
}

/**
 * Configures the internal settings of the library through configs from remote server and local settings from developers.
 * Clients must call this whenever remote configuration is fetched from GxT server.
 * If there is any configuration set by dev users, clients must call this API during library initialization stage.
 *
 * The SessionControlConfigData.overrideData is a string of feature toggle/options separated by '&' character.
 * The options are:
 *      log=enable|disable              -- Enable log generation from the library.
 *      console=enable|disable          -- Enable console log from the library if log generation is enabled.
 *      hdr=on|off                      -- Force request HDR virtual monitors on server.
 *  Instead of enable/disable, on/off or 0/1 values can also be used.
 * @param data - configuration parameters.
 */
export function configureSessionControlSettings(data: SessionControlConfigData) {
    Log.i("{11b33a0}", "{e65e133}", JSON.stringify(data));
    ConfigureRtcUtilsSettings(data);
    // Note: This function is at app start, let the exception bubble up and fail.
    //       The remote config data can be handled within try catch as it will be part of prod.
    if (data.gxtOverrideData) {
        const configData = <GxtRemoteConfigData>JSON.parse(data.gxtOverrideData);
        // Parse the 'pmCommunication' config block
        if (configData.params) {
            for (const element of configData.params) {
                if (element.name === "pmCommunication") {
                    SessionControlSettings.commonConfig.pmCommunication = <PmCommunication>(
                        element.value
                    );
                    // Set the override config details for telemetry, only when we have some override settings
                    SessionControlSettings.remoteOverrideInfo = {
                        type: OverrideConfigTypeEnum.GXT,
                        version: element.version
                    };
                }
            }
        }
    }

    if (data.overrideData) {
        const settings = data.overrideData.toLowerCase();
        const settingsMap = new URLSearchParams(settings);

        const getSettingBool = (key: string): boolean | undefined => {
            const value: any = settingsMap.get(key);
            return toBool(value);
        };

        SessionControlSettings.loggingEnabled =
            getSettingBool("log") ?? SessionControlSettings.loggingEnabled;
        SessionControlSettings.consoleLoggingEnabled =
            SessionControlSettings.loggingEnabled &&
            (getSettingBool("console") ?? SessionControlSettings.consoleLoggingEnabled);

        // ClientSDK is using 'websocketsignaling' reference, delete this once thats removed.
        SessionControlSettings.webSocketSignaling =
            getSettingBool("websocketsignaling") ?? SessionControlSettings.webSocketSignaling;
        SessionControlSettings.webRtcStreamer =
            getSettingBool("webrtcstreamer") ?? SessionControlSettings.webRtcStreamer;
        SessionControlSettings.hdr = getSettingBool("hdr") ?? SessionControlSettings.hdr;
    }
}

// todo: legacy API and struct to be deleted eventually

/**
 * @deprecated please use configureSessionControlSettings
 */
export function ConfigureGridServerSettings(data: GridServerConfigData) {
    configureSessionControlSettings(data);
}

export type GridServerConfigData = SessionControlConfigData;
export { SessionControlSettings as GridServerSettings };
